import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XIcon,
  ChevronLeftIcon,
  PlusIcon,
  Trash2Icon,
  ClockIcon,
  ActivityIcon,
  PowerIcon,
  MailIcon,
  MessageSquareIcon,
  CheckIcon,
  SparklesIcon,
  ZapIcon,
  ThermometerIcon,
  DropletsIcon,
  PersonStandingIcon,
  WindIcon,
  SlidersIcon,
  MoonIcon,
  SunIcon,
  FlameIcon,
  AlertTriangleIcon,
  HomeIcon,
  Building2Icon
} from 'lucide-react';
import {
  SceneRule,
  SceneCondition,
  SceneAction,
  ConditionLogic,
  ConditionType,
  ActionType,
  SensorKey,
  ComparisonOperator,
  DeviceCommand
} from '../types/scene';
import { Sensor } from './FloorPlanHeatMap';
import { getFriendlyDeviceTypeName } from './SensorListView';

export interface ZoneOption {
  id: string;
  name: string;
  sensors: Sensor[];
}

interface SceneEditorModalProps {
  isOpen: boolean;
  scene?: SceneRule | null;
  allSensors: Sensor[];
  rooms?: ZoneOption[];
  defaultRoomId?: string;
  apartmentLabel?: string;
  onClose: () => void;
  onSave: (savedScene: SceneRule) => void;
}

// Icons selector removed

const DAYS_OF_WEEK = [
  { id: 1, label: '월' },
  { id: 2, label: '화' },
  { id: 3, label: '수' },
  { id: 4, label: '목' },
  { id: 5, label: '금' },
  { id: 6, label: '토' },
  { id: 0, label: '일' },
];

const SENSOR_KEYS: { id: SensorKey; label: string; unit: string; icon: any }[] = [
  { id: 'temperature', label: '온도', unit: '°C', icon: ThermometerIcon },
  { id: 'humidity', label: '습도', unit: '%', icon: DropletsIcon },
  { id: 'power_instant', label: '실시간 전력', unit: 'W', icon: ZapIcon },
  { id: 'presence_state', label: '재실 상태', unit: '', icon: PersonStandingIcon },
  { id: 'CO2', label: 'CO2 농도', unit: 'ppm', icon: WindIcon },
  { id: 'dust', label: '미세먼지', unit: '㎍/㎥', icon: ActivityIcon },
];

const OPERATORS: { id: ComparisonOperator; label: string }[] = [
  { id: '>=', label: '이상 (>=)' },
  { id: '>', label: '초과 (>)' },
  { id: '<=', label: '이하 (<=)' },
  { id: '<', label: '미만 (<)' },
  { id: '==', label: '일치 (==)' },
];

const TYPE_ORDER: Record<string, number> = {
  'SEM': 1,
  'SEO': 2,
  'SES': 3,
  'SEL': 4,
  'SER': 5,
  'SEC': 6,
  'CURTAIN': 7,
  'BLIND': 8,
  'HERV': 9,
};

const naturalSort = (a: any, b: any) => {
  if (typeof a !== 'string' || typeof b !== 'string') return 0;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

function getDeviceIconComponent(type: string) {
  switch (type?.toUpperCase()) {
    case 'SEM': return PowerIcon;
    case 'SEO': return ZapIcon;
    case 'SES': return SlidersIcon;
    case 'SEL': return SunIcon;
    case 'SER': return ActivityIcon;
    case 'SEC':
    case 'CURTAIN':
    case 'BLIND': return WindIcon;
    default: return ZapIcon;
  }
}

export function SceneEditorModal({
  isOpen,
  scene,
  allSensors,
  rooms = [],
  defaultRoomId,
  apartmentLabel = '전체공간',
  onClose,
  onSave
}: SceneEditorModalProps) {


  // Form States
  // Sync form when scene changes
  useEffect(() => {
    if (isOpen) {
      setName(scene?.name || '');
      setDescription(scene?.description || '');
      setIcon(scene?.icon || 'Sparkles');
      setConditionLogic(scene?.conditionLogic || 'AND');
      setConditions(scene?.conditions || []);
      setActions(scene?.actions || []);

    }
  }, [isOpen, scene, defaultRoomId]);

  // Zone / Scope automatically determined by selected top-left room or scene
  const availableZones = useMemo(() => (Array.isArray(rooms) ? rooms : []).filter(r => r && r.id !== 'ALL'), [rooms]);

  const effectiveZoneId = useMemo(() => {
    if (scene?.zoneId) return scene.zoneId;
    if (defaultRoomId && defaultRoomId !== 'ALL') return defaultRoomId;
    return '';
  }, [scene, defaultRoomId]);

  const isUnitScope = !effectiveZoneId;
  const targetScope: 'unit' | 'zone' = isUnitScope ? 'unit' : 'zone';
  const targetZoneObj = useMemo(() => availableZones.find(z => z.id === effectiveZoneId), [availableZones, effectiveZoneId]);
  const currentZoneName = isUnitScope ? '전체공간' : (targetZoneObj?.name || scene?.zoneName || '선택 공간');

  // Calculate scoped sensors strictly based on active scope
  const scopedSensors = useMemo(() => {
    const safeAllSensors = allSensors || [];
    if (isUnitScope || !effectiveZoneId) {
      return safeAllSensors;
    }
    if (targetZoneObj && Array.isArray(targetZoneObj.sensors) && targetZoneObj.sensors.length > 0) {
      return targetZoneObj.sensors;
    }
    // Fallback: match by roomId or roomName in allSensors
    const matched = safeAllSensors.filter(s => s.roomId === effectiveZoneId || (targetZoneObj && (s.roomId === targetZoneObj.id || s.roomName === targetZoneObj.name)));
    return matched.length > 0 ? matched : safeAllSensors;
  }, [isUnitScope, effectiveZoneId, targetZoneObj, allSensors]);

  // Controllable devices list for selected scope
  const controllableSensors = useMemo(() => {
    return (scopedSensors || []).filter(s => {
      if (!s) return false;
      const t = s.type?.toUpperCase();
      return ['SEO', 'SEM', 'SES', 'SEL', 'SER', 'SEC', 'CURTAIN', 'BLIND'].includes(t);
    });
  }, [scopedSensors]);

  // Sorted controllable sensors strictly grouped by device type then natural sort
  const sortedControllableSensors = useMemo(() => {
    return [...controllableSensors].sort((a, b) => {
      const typeA = TYPE_ORDER[a.type?.toUpperCase()] || 99;
      const typeB = TYPE_ORDER[b.type?.toUpperCase()] || 99;
      if (typeA !== typeB) return typeA - typeB;
      return naturalSort(a.label || a.id, b.label || b.id);
    });
  }, [controllableSensors]);

  const [name, setName] = useState(scene?.name || '');
  const [description, setDescription] = useState(scene?.description || '');
  const [icon, setIcon] = useState(scene?.icon || 'Sparkles');
  const [conditionLogic, setConditionLogic] = useState<ConditionLogic>(scene?.conditionLogic || 'AND');

  const [conditions, setConditions] = useState<SceneCondition[]>(
    scene?.conditions || []
  );
  const [actions, setActions] = useState<SceneAction[]>(
    scene?.actions || []
  );

  // Sub-modal or condition picker states
  const [isAddingCondition, setIsAddingCondition] = useState(false);
  const [conditionType, setConditionType] = useState<ConditionType>('schedule');

  // Temp condition form
  const [tempTime, setTempTime] = useState('08:00');
  const [tempDays, setTempDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [tempDeviceId, setTempDeviceId] = useState<string>(scopedSensors[0]?.id || '');
  const [tempSensorKey, setTempSensorKey] = useState<SensorKey>('temperature');
  const [tempOperator, setTempOperator] = useState<ComparisonOperator>('>=');
  const [tempSensorValue, setTempSensorValue] = useState<string>('26');

  // Sub-modal or action picker states
  const [isAddingAction, setIsAddingAction] = useState(false);
  const [actionType, setActionType] = useState<ActionType>('device_control');

  // Temp action form
  const [tempSelectedDeviceIds, setTempSelectedDeviceIds] = useState<string[]>([]);
  const [tempDeviceCommand, setTempDeviceCommand] = useState<DeviceCommand>('turn_off');
  const [tempModeValue, setTempModeValue] = useState<string>('normal');
  const [tempRecipient, setTempRecipient] = useState('');
  const [tempMessage, setTempMessage] = useState('');
  const [tempTitle, setTempTitle] = useState('');

  if (!isOpen) return null;

  // Add Condition Handler
  const handleAddCondition = () => {
    if (conditionType === 'schedule') {
      const newCond: SceneCondition = {
        id: `cond-${Date.now()}`,
        type: 'schedule',
        schedule: {
          time: tempTime,
          days: tempDays.length > 0 ? tempDays : [0, 1, 2, 3, 4, 5, 6],
        }
      };
      setConditions(prev => [...prev, newCond]);
    } else {
      const targetDev = scopedSensors.find(s => s.id === tempDeviceId) || scopedSensors[0];
      const keyObj = SENSOR_KEYS.find(k => k.id === tempSensorKey);
      const newCond: SceneCondition = {
        id: `cond-${Date.now()}`,
        type: 'sensor',
        sensor: {
          deviceId: targetDev?.id || '',
          deviceName: targetDev?.label || '센서',
          key: tempSensorKey,
          operator: tempOperator,
          value: Number(tempSensorValue) || (tempSensorValue === 'true' ? 1 : 0),
          unit: keyObj?.unit || ''
        }
      };
      setConditions(prev => [...prev, newCond]);
    }
    setIsAddingCondition(false);
  };

  const handleRemoveCondition = (id: string) => {
    setConditions(prev => prev.filter(c => c.id !== id));
  };

  // Add Action Handler
  const handleAddAction = () => {
    if (actionType === 'device_control') {
      if (tempSelectedDeviceIds.length === 0) return;
      const selectedDevs = scopedSensors.filter(s => tempSelectedDeviceIds.includes(s.id));
      const newAction: SceneAction = {
        id: `action-${Date.now()}`,
        type: 'device_control',
        deviceControl: {
          deviceIds: selectedDevs.map(s => s.id),
          deviceNames: selectedDevs.map(s => s.label),
          deviceTypes: selectedDevs.map(s => s.type),
          command: tempDeviceCommand,
          modeValue: tempDeviceCommand === 'set_mode' ? tempModeValue : undefined
        }
      };
      setActions(prev => [...prev, newAction]);
    } else if (actionType === 'send_email') {
      if (!tempRecipient) return;
      const newAction: SceneAction = {
        id: `action-${Date.now()}`,
        type: 'send_email',
        notification: {
          recipient: tempRecipient,
          title: tempTitle || `${name || '시나리오'} 알림`,
          message: tempMessage || '시나리오가 실행되었습니다.'
        }
      };
      setActions(prev => [...prev, newAction]);
    } else if (actionType === 'send_sms') {
      if (!tempRecipient) return;
      const newAction: SceneAction = {
        id: `action-${Date.now()}`,
        type: 'send_sms',
        notification: {
          recipient: tempRecipient,
          message: tempMessage || `[알림] ${name || '시나리오'}가 자동 실행되었습니다.`
        }
      };
      setActions(prev => [...prev, newAction]);
    }
    setIsAddingAction(false);
    setTempSelectedDeviceIds([]);
    setTempRecipient('');
    setTempMessage('');
  };

  const handleRemoveAction = (id: string) => {
    setActions(prev => prev.filter(a => a.id !== id));
  };

  // Save Scene
  const handleSave = () => {
    if (!name.trim()) return;
    const finalScene: SceneRule = {
      id: scene?.id || `scene-${Date.now()}`,
      name: name.trim(),
      description: description.trim(),
      icon,
      enabled: scene ? scene.enabled : true,
      conditionLogic,
      conditions,
      actions,
      targetScope,
      zoneId: !isUnitScope ? effectiveZoneId : undefined,
      zoneName: !isUnitScope ? (targetZoneObj?.name || scene?.zoneName || '공간') : '전체공간',
      lastTriggered: scene?.lastTriggered,
      createdAt: scene?.createdAt || Date.now(),
    };
    onSave(finalScene);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[500] flex justify-center"
      style={{ backgroundColor: 'var(--mobile-bg, #0b111e)' }}
    >
      <div
        className="w-full max-w-md h-[100dvh] max-h-[100dvh] flex flex-col shadow-2xl relative overflow-hidden"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          backgroundColor: 'var(--mobile-bg, #0b111e)',
          color: 'var(--mobile-text-primary, #ffffff)'
        }}
      >
        {/* ─── Top Header Bar (Back + Title + Save) ─── */}
        <div
          className="h-14 flex items-center justify-between px-4 border-b flex-shrink-0 z-10"
          style={{ borderColor: 'var(--mobile-border)', backgroundColor: 'var(--mobile-bg-header)' }}
        >
          <button
            onClick={onClose}
            className="p-2 -ml-2 rounded-lg text-slate-400 hover:text-white active:scale-95 transition-colors flex items-center gap-1"
            title="돌아가기"
          >
            <ChevronLeftIcon className="w-6 h-6 text-slate-300" />
            <span className="text-xs font-medium text-slate-300">취소</span>
          </button>
          <h2 className="text-base font-bold text-cyan-400">
            {scene ? '시나리오 수정' : '새 시나리오 만들기'}
          </h2>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all shadow-md ${
              name.trim()
                ? 'bg-cyan-500 text-black shadow-cyan-500/20 active:scale-95'
                : 'bg-white/10 text-slate-500 cursor-not-allowed'
            }`}
          >
            저장
          </button>
        </div>

      {/* ─── Scrollable Body ─── */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5 pb-36 overscroll-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                {/* 0. Target Zone Info Badge (Automatically derived from top-left room selector) */}
        <div className="glass-panel rounded-2xl p-3.5 border border-white/10 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2.5">
            <div className={`p-2 rounded-xl ${isUnitScope ? 'bg-indigo-500/20 text-indigo-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
              {isUnitScope ? <HomeIcon className="w-4 h-4" /> : <Building2Icon className="w-4 h-4" />}
            </div>
            <div>
              <div className="text-[0.68rem] text-slate-400 font-medium">적용 공간</div>
              <div className="text-xs font-bold text-white flex items-center gap-1.5">
                <span>{currentZoneName}</span>
                <span className="text-[0.65rem] font-medium text-cyan-300 font-mono">
                  ({scopedSensors.length}개 기기 대상)
                </span>
              </div>
            </div>
          </div>
          <div className={`text-[0.65rem] px-2.5 py-0.5 rounded-full font-bold border ${
            isUnitScope
              ? 'bg-indigo-500/10 border-indigo-500/30 text-indigo-300'
              : 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
          }`}>
            {isUnitScope ? '전체공간' : '구역 전용'}
          </div>
        </div>

        {/* 1. Basic Info (Name, Desc, Icon) */}
        <div className="glass-panel rounded-2xl p-4 space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-400 mb-1.5">
              시나리오 이름 <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 퇴근 모드, 고온 자동 냉방"
              className="w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm font-medium text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-400 mb-1.5">설명 (선택)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="시나리오 동작 요약 설명"
              className="w-full px-3.5 py-2 rounded-xl bg-white/5 border border-white/10 text-xs font-medium text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 transition-colors"
            />
          </div>


        </div>

        {/* 2. Conditions Section (WHEN / IF) */}
        <div className="glass-panel rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-indigo-500/20 text-indigo-400">
                <ClockIcon className="w-4 h-4" />
              </div>
              <span className="text-sm font-bold text-white">조건 (WHEN)</span>
            </div>

            {/* AND / OR Logic Switch */}
            <div className="flex items-center bg-black/30 rounded-lg p-0.5 border border-white/10">
              <button
                type="button"
                onClick={() => setConditionLogic('AND')}
                className={`px-2.5 py-1 rounded-md text-[0.7rem] font-bold transition-all ${
                  conditionLogic === 'AND'
                    ? 'bg-indigo-500 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                AND (모두 일치)
              </button>
              <button
                type="button"
                onClick={() => setConditionLogic('OR')}
                className={`px-2.5 py-1 rounded-md text-[0.7rem] font-bold transition-all ${
                  conditionLogic === 'OR'
                    ? 'bg-indigo-500 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                OR (하나라도 일치)
              </button>
            </div>
          </div>

          <p className="text-[0.72rem] text-slate-400">
            {conditionLogic === 'AND'
              ? '설정된 모든 조건이 동시에 만족될 때 동작이 실행됩니다.'
              : '설정된 조건 중 하나라도 충족되면 동작이 실행됩니다.'}
          </p>

          {/* Condition Items List */}
          <div className="space-y-2">
            {conditions.map((cond, idx) => (
              <div
                key={cond.id}
                className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-400">
                    {cond.type === 'schedule' ? (
                      <ClockIcon className="w-4 h-4" />
                    ) : (
                      <ActivityIcon className="w-4 h-4" />
                    )}
                  </div>
                  <div>
                    {cond.type === 'schedule' && cond.schedule && (
                      <div>
                        <div className="text-xs font-bold text-white">
                          매일 {cond.schedule.time}
                        </div>
                        <div className="text-[0.68rem] text-slate-400">
                          반복:{' '}
                          {cond.schedule.days.length === 7
                            ? '매일'
                            : cond.schedule.days.length === 5 && !cond.schedule.days.includes(0) && !cond.schedule.days.includes(6)
                            ? '평일'
                            : cond.schedule.days
                                .map((d) => DAYS_OF_WEEK.find((dw) => dw.id === d)?.label)
                                .join(', ')}
                        </div>
                      </div>
                    )}
                    {cond.type === 'sensor' && cond.sensor && (
                      <div>
                        <div className="text-xs font-bold text-white">
                          {cond.sensor.deviceName} · {SENSOR_KEYS.find((k) => k.id === cond.sensor?.key)?.label}
                        </div>
                        <div className="text-[0.68rem] text-cyan-400 font-mono">
                          {cond.sensor.operator} {cond.sensor.value} {cond.sensor.unit}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleRemoveCondition(cond.id)}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 active:scale-90 transition-colors"
                >
                  <Trash2Icon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add Condition Button */}
          {!isAddingCondition ? (
            <button
              type="button"
              onClick={() => setIsAddingCondition(true)}
              className="w-full py-2.5 rounded-xl border border-dashed border-white/20 hover:border-indigo-400/50 bg-white/5 text-xs font-bold text-indigo-300 flex items-center justify-center gap-1.5 active:scale-95 transition-all"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              <span>조건 추가하기</span>
            </button>
          ) : (
            /* Condition Form Inline */
            <div className="p-3.5 rounded-xl bg-black/40 border border-indigo-500/30 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-indigo-300">새 조건 설정</span>
                <div className="flex rounded-lg p-0.5 bg-white/5 border border-white/10">
                  <button
                    type="button"
                    onClick={() => setConditionType('schedule')}
                    className={`px-2.5 py-0.5 rounded text-[0.7rem] font-semibold ${
                      conditionType === 'schedule' ? 'bg-indigo-500 text-white' : 'text-slate-400'
                    }`}
                  >
                    시간 스케줄
                  </button>
                  <button
                    type="button"
                    onClick={() => setConditionType('sensor')}
                    className={`px-2.5 py-0.5 rounded text-[0.7rem] font-semibold ${
                      conditionType === 'sensor' ? 'bg-indigo-500 text-white' : 'text-slate-400'
                    }`}
                  >
                    센서/환경
                  </button>
                </div>
              </div>

              {conditionType === 'schedule' ? (
                <div className="space-y-2.5">
                  {/* 3-Part Time Picker: AM/PM + 1~12 Hour + 5-min Interval Minute */}
                  <div>
                    {(() => {
                      const rawHour = parseInt((tempTime || '08:00').split(':')[0] || '8', 10);
                      const isPM = rawHour >= 12;
                      const hour12 = rawHour % 12 === 0 ? 12 : rawHour % 12;
                      const rawMin = parseInt((tempTime || '08:00').split(':')[1] || '0', 10);
                      const roundedMin = Math.round(rawMin / 5) * 5 % 60;
                      const minuteStr = String(roundedMin).padStart(2, '0');
                      const ampmLabel = isPM ? '오후' : '오전';

                      const updateTime = (newIsPM: boolean, newHour12: number, newMin: string) => {
                        let finalH = newHour12;
                        if (newIsPM) {
                          finalH = newHour12 === 12 ? 12 : newHour12 + 12;
                        } else {
                          finalH = newHour12 === 12 ? 0 : newHour12;
                        }
                        setTempTime(`${String(finalH).padStart(2, '0')}:${newMin}`);
                      };

                      return (
                        <div className="space-y-1.5">
                          <label className="block text-[0.7rem] text-slate-400 flex items-center justify-between">
                            <span>실행 시간 (5분 단위)</span>
                            <span className="text-[0.7rem] font-bold text-indigo-300 font-mono">
                              {ampmLabel} {hour12}:{minuteStr} ({tempTime || '08:00'})
                            </span>
                          </label>

                          <div className="grid grid-cols-3 gap-2">
                            {/* 1. AM/PM Dropdown */}
                            <div className="flex items-center bg-white/5 border border-white/10 rounded-xl px-2.5 py-1.5">
                              <select
                                value={isPM ? 'PM' : 'AM'}
                                onChange={(e) => updateTime(e.target.value === 'PM', hour12, minuteStr)}
                                className="w-full bg-transparent text-xs font-bold text-cyan-300 focus:outline-none cursor-pointer"
                              >
                                <option value="AM" className="bg-slate-900 text-cyan-300">
                                  오전
                                </option>
                                <option value="PM" className="bg-slate-900 text-cyan-300">
                                  오후
                                </option>
                              </select>
                            </div>

                            {/* 2. 1~12 Hour Dropdown */}
                            <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl px-2.5 py-1.5">
                              <select
                                value={hour12}
                                onChange={(e) => updateTime(isPM, parseInt(e.target.value, 10), minuteStr)}
                                className="w-full bg-transparent text-xs font-bold text-white focus:outline-none font-mono cursor-pointer"
                              >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                                  <option key={h} value={h} className="bg-slate-900 text-white">
                                    {h}시
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* 3. 5-Minute Dropdown */}
                            <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl px-2.5 py-1.5">
                              <select
                                value={minuteStr}
                                onChange={(e) => updateTime(isPM, hour12, e.target.value)}
                                className="w-full bg-transparent text-xs font-bold text-indigo-300 focus:outline-none font-mono cursor-pointer"
                              >
                                {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map((m) => (
                                  <option key={m} value={m} className="bg-slate-900 text-indigo-300">
                                    {m}분
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <label className="block text-[0.7rem] text-slate-400 mb-1">반복 요일</label>
                    <div className="flex items-center gap-1">
                      {DAYS_OF_WEEK.map((d) => {
                        const isDayActive = tempDays.includes(d.id);
                        return (
                          <button
                            key={d.id}
                            type="button"
                            onClick={() => {
                              setTempDays((prev) =>
                                isDayActive ? prev.filter((item) => item !== d.id) : [...prev, d.id]
                              );
                            }}
                            className={`w-7 h-7 rounded-lg text-xs font-bold flex items-center justify-center transition-all ${
                              isDayActive
                                ? 'bg-indigo-500 text-white'
                                : 'bg-white/5 border border-white/10 text-slate-400'
                            }`}
                          >
                            {d.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2.5">
                  <div>
                    <label className="block text-[0.7rem] text-slate-400 mb-1">대상 센서 선택</label>
                    <select
                      value={tempDeviceId}
                      onChange={(e) => setTempDeviceId(e.target.value)}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-[#1a2333] border border-white/10 text-xs text-white"
                    >
                      {allSensors.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label} ({getFriendlyDeviceTypeName(s.type)})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[0.7rem] text-slate-400 mb-1">측정 항목</label>
                      <select
                        value={tempSensorKey}
                        onChange={(e) => setTempSensorKey(e.target.value as SensorKey)}
                        className="w-full px-2 py-1.5 rounded-lg bg-[#1a2333] border border-white/10 text-xs text-white"
                      >
                        {SENSOR_KEYS.map((k) => (
                          <option key={k.id} value={k.id}>
                            {k.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[0.7rem] text-slate-400 mb-1">연산자</label>
                      <select
                        value={tempOperator}
                        onChange={(e) => setTempOperator(e.target.value as ComparisonOperator)}
                        className="w-full px-2 py-1.5 rounded-lg bg-[#1a2333] border border-white/10 text-xs text-white"
                      >
                        {OPERATORS.map((op) => (
                          <option key={op.id} value={op.id}>
                            {op.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[0.7rem] text-slate-400 mb-1">기준값</label>
                      <input
                        type="number"
                        value={tempSensorValue}
                        onChange={(e) => setTempSensorValue(e.target.value)}
                        placeholder="예: 26"
                        className="w-full px-2 py-1.5 rounded-lg bg-white/10 border border-white/10 text-xs text-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setIsAddingCondition(false)}
                  className="px-3 py-1 rounded-lg text-xs text-slate-400 hover:text-white"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleAddCondition}
                  className="px-3 py-1 rounded-lg bg-indigo-500 text-white text-xs font-bold active:scale-95"
                >
                  조건 적용
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 3. Actions Section (THEN / ACTION) */}
        <div className="glass-panel rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400">
              <PowerIcon className="w-4 h-4" />
            </div>
            <span className="text-sm font-bold text-white">실행 동작 (THEN)</span>
          </div>

          <p className="text-[0.72rem] text-slate-400">
            조건이 충족될 때 실행할 제어 명령 및 알림을 지정합니다.
          </p>

          {/* Action Items List */}
          <div className="space-y-2">
            {actions.map((act) => (
              <div
                key={act.id}
                className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400">
                    {act.type === 'device_control' ? (
                      <PowerIcon className="w-4 h-4" />
                    ) : act.type === 'send_email' ? (
                      <MailIcon className="w-4 h-4" />
                    ) : (
                      <MessageSquareIcon className="w-4 h-4" />
                    )}
                  </div>
                  <div>
                    {act.type === 'device_control' && act.deviceControl && (
                      <div>
                        <div className="text-xs font-bold text-white">
                          기기 제어 ({act.deviceControl.deviceNames.length}개 기기)
                        </div>
                        <div className="text-[0.68rem] text-emerald-400 font-medium">
                          {act.deviceControl.command === 'turn_on'
                            ? '전원 켜기 (ON)'
                            : act.deviceControl.command === 'turn_off'
                            ? '전원 끄기 (OFF)'
                            : `모드 변경: ${act.deviceControl.modeValue}`}
                        </div>
                        <div className="text-[0.65rem] text-slate-400 truncate max-w-[200px]">
                          {act.deviceControl.deviceNames.join(', ')}
                        </div>
                      </div>
                    )}
                    {act.type === 'send_email' && act.notification && (
                      <div>
                        <div className="text-xs font-bold text-white">이메일 발송</div>
                        <div className="text-[0.68rem] text-cyan-400">{act.notification.recipient}</div>
                        <div className="text-[0.65rem] text-slate-400 truncate max-w-[200px]">
                          {act.notification.message}
                        </div>
                      </div>
                    )}
                    {act.type === 'send_sms' && act.notification && (
                      <div>
                        <div className="text-xs font-bold text-white">문자(SMS) 발송</div>
                        <div className="text-[0.68rem] text-cyan-400">{act.notification.recipient}</div>
                        <div className="text-[0.65rem] text-slate-400 truncate max-w-[200px]">
                          {act.notification.message}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleRemoveAction(act.id)}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 active:scale-90 transition-colors"
                >
                  <Trash2Icon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add Action Button */}
          {!isAddingAction ? (
            <button
              type="button"
              onClick={() => setIsAddingAction(true)}
              className="w-full py-2.5 rounded-xl border border-dashed border-white/20 hover:border-emerald-400/50 bg-white/5 text-xs font-bold text-emerald-300 flex items-center justify-center gap-1.5 active:scale-95 transition-all"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              <span>동작 추가하기</span>
            </button>
          ) : (
            /* Action Form Inline */
            <div className="p-3.5 rounded-xl bg-black/40 border border-emerald-500/30 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-emerald-300">새 동작 설정</span>
                <div className="flex rounded-lg p-0.5 bg-white/5 border border-white/10">
                  <button
                    type="button"
                    onClick={() => setActionType('device_control')}
                    className={`px-2.5 py-0.5 rounded text-[0.7rem] font-semibold ${
                      actionType === 'device_control' ? 'bg-emerald-500 text-black font-bold' : 'text-slate-400'
                    }`}
                  >
                    기기 제어
                  </button>
                  <button
                    type="button"
                    onClick={() => setActionType('send_email')}
                    className={`px-2.5 py-0.5 rounded text-[0.7rem] font-semibold ${
                      actionType === 'send_email' ? 'bg-emerald-500 text-black font-bold' : 'text-slate-400'
                    }`}
                  >
                    이메일
                  </button>
                  <button
                    type="button"
                    onClick={() => setActionType('send_sms')}
                    className={`px-2.5 py-0.5 rounded text-[0.7rem] font-semibold ${
                      actionType === 'send_sms' ? 'bg-emerald-500 text-black font-bold' : 'text-slate-400'
                    }`}
                  >
                    문자(SMS)
                  </button>
                </div>
              </div>

              {actionType === 'device_control' ? (
                <div className="space-y-3">
                  {/* Select Multiple Devices (Round Square Icon Tile Grid) */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[0.72rem] font-bold text-slate-300 flex items-center gap-1.5">
                        <span>제어할 기기 선택</span>
                        <span className="text-[0.68rem] font-bold text-emerald-400 font-mono">
                          ({tempSelectedDeviceIds.length}/{sortedControllableSensors.length})
                        </span>
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          if (tempSelectedDeviceIds.length === sortedControllableSensors.length) {
                            setTempSelectedDeviceIds([]);
                          } else {
                            setTempSelectedDeviceIds(sortedControllableSensors.map(s => s.id));
                          }
                        }}
                        className="text-[0.68rem] font-bold text-cyan-400 hover:text-cyan-300 active:scale-95 transition-transform"
                      >
                        {tempSelectedDeviceIds.length === sortedControllableSensors.length ? '선택 해제' : '전체 선택'}
                      </button>
                    </div>

                    <div
                      className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto p-1.5 rounded-2xl bg-black/40 border border-white/10 overscroll-contain"
                      style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      {sortedControllableSensors.length === 0 ? (
                        <div className="col-span-2 py-6 text-center text-slate-500 text-xs">
                          해당 공간에 제어 가능한 기기가 없습니다.
                        </div>
                      ) : (
                        sortedControllableSensors.map((dev) => {
                          const isSelected = tempSelectedDeviceIds.includes(dev.id);
                          const DevIcon = getDeviceIconComponent(dev.type);
                          return (
                            <div
                              key={dev.id}
                              onClick={() => {
                                setTempSelectedDeviceIds((prev) =>
                                  isSelected ? prev.filter((id) => id !== dev.id) : [...prev, dev.id]
                                );
                              }}
                              className={`relative flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-all active:scale-95 select-none ${
                                isSelected
                                  ? 'bg-emerald-500/20 border-emerald-400 text-white shadow-sm shadow-emerald-500/20 ring-1 ring-emerald-400/50'
                                  : 'bg-white/5 border-white/10 hover:border-white/20 text-slate-300 hover:bg-white/10'
                              }`}
                            >
                              <div
                                className={`p-1.5 rounded-lg flex-shrink-0 transition-colors ${
                                  isSelected ? 'bg-emerald-400 text-black font-bold' : 'bg-white/10 text-cyan-400'
                                }`}
                              >
                                <DevIcon className="w-4 h-4" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-bold truncate leading-tight">
                                  {dev.label}
                                </div>
                                <div className="text-[0.65rem] text-slate-400 font-mono mt-0.5">
                                  {getFriendlyDeviceTypeName(dev.type)}
                                </div>
                              </div>
                              {isSelected && (
                                <div className="w-4 h-4 rounded-full bg-emerald-400 text-black flex items-center justify-center flex-shrink-0">
                                  <CheckIcon className="w-3 h-3 stroke-[3]" />
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Command */}
                  <div>
                    <label className="block text-[0.7rem] text-slate-400 mb-1">동작 명령</label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setTempDeviceCommand('turn_on')}
                        className={`py-1.5 rounded-lg text-xs font-bold border transition-all ${
                          tempDeviceCommand === 'turn_on'
                            ? 'bg-emerald-500/20 border-emerald-400 text-emerald-300'
                            : 'border-white/10 bg-white/5 text-slate-400'
                        }`}
                      >
                        켜기 (ON)
                      </button>
                      <button
                        type="button"
                        onClick={() => setTempDeviceCommand('turn_off')}
                        className={`py-1.5 rounded-lg text-xs font-bold border transition-all ${
                          tempDeviceCommand === 'turn_off'
                            ? 'bg-red-500/20 border-red-400 text-red-300'
                            : 'border-white/10 bg-white/5 text-slate-400'
                        }`}
                      >
                        끄기 (OFF)
                      </button>
                      <button
                        type="button"
                        onClick={() => setTempDeviceCommand('set_mode')}
                        className={`py-1.5 rounded-lg text-xs font-bold border transition-all ${
                          tempDeviceCommand === 'set_mode'
                            ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300'
                            : 'border-white/10 bg-white/5 text-slate-400'
                        }`}
                      >
                        모드 변경
                      </button>
                    </div>
                  </div>

                  {tempDeviceCommand === 'set_mode' && (
                    <div>
                      <label className="block text-[0.7rem] text-slate-400 mb-1">설정할 모드</label>
                      <select
                        value={tempModeValue}
                        onChange={(e) => setTempModeValue(e.target.value)}
                        className="w-full px-2.5 py-1.5 rounded-lg bg-[#1a2333] border border-white/10 text-xs text-white"
                      >
                        <option value="occupancy">재실 모드</option>
                        <option value="normal">일반 모드</option>
                        <option value="ai">AI 모드</option>
                      </select>
                    </div>
                  )}
                </div>
              ) : actionType === 'send_email' ? (
                <div className="space-y-2">
                  <div>
                    <label className="block text-[0.7rem] text-slate-400 mb-1">수신 이메일</label>
                    <input
                      type="email"
                      value={tempRecipient}
                      onChange={(e) => setTempRecipient(e.target.value)}
                      placeholder="admin@company.com"
                      className="w-full px-2.5 py-1.5 rounded-lg bg-white/10 border border-white/10 text-xs text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[0.7rem] text-slate-400 mb-1">메시지 내용</label>
                    <input
                      type="text"
                      value={tempMessage}
                      onChange={(e) => setTempMessage(e.target.value)}
                      placeholder="시나리오 실행 시 전송될 내용"
                      className="w-full px-2.5 py-1.5 rounded-lg bg-white/10 border border-white/10 text-xs text-white"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <label className="block text-[0.7rem] text-slate-400 mb-1">수신 전화번호</label>
                    <input
                      type="tel"
                      value={tempRecipient}
                      onChange={(e) => setTempRecipient(e.target.value)}
                      placeholder="010-1234-5678"
                      className="w-full px-2.5 py-1.5 rounded-lg bg-white/10 border border-white/10 text-xs text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[0.7rem] text-slate-400 mb-1">문자 메시지</label>
                    <input
                      type="text"
                      value={tempMessage}
                      onChange={(e) => setTempMessage(e.target.value)}
                      placeholder="[알림] 시나리오가 실행되었습니다."
                      className="w-full px-2.5 py-1.5 rounded-lg bg-white/10 border border-white/10 text-xs text-white"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setIsAddingAction(false)}
                  className="px-3 py-1 rounded-lg text-xs text-slate-400 hover:text-white"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleAddAction}
                  className="px-3 py-1 rounded-lg bg-emerald-500 text-black font-bold text-xs active:scale-95"
                >
                  동작 적용
                </button>
              </div>
            </div>
          )}
        </div>


      </div>
      </div>
    </div>
  );
}
